import { lazy, StrictMode, Suspense } from 'react';
import { render } from 'react-dom';
import createSdk, { addInjectionPoints } from 'apps/widget/sdk';
import { loadStringCatalog, StringsProvider } from 'state/strings';
import { WidgetConfiguration } from 'types';
import {
  injectWidgets as injectWidgetsUtil,
  getInjectionPoints,
} from 'utils/injection';
import { InjectWidgetsData } from 'utils/injection/types';

// NB: It's important this this is a lazy import.  If not lazy, all of the styles
// associated with the component tree will be injected at import time, which is
// before the shadow dom gets created below.  Since styles have to be injected into
// the shadow dom (see webpack config), we have to ensure shadow dom exists before
// importing any styles
const importApp = () => import('./App');
const App = lazy(importApp);

async function renderWidget({
  clientIp,
  engagementTrackingUrl,
  locale,
  root,
  sessionId,
  title,
  url,
  bundleId,
  variantId,
  widgetId,
}: InjectWidgetsData) {
  // the App chunk is requested alongside the string catalog so that the first
  // render waits on both without loading them one after the other
  const [strings] = await Promise.all([loadStringCatalog(locale), importApp()]);

  // declares the language of the rendered catalog on the react root so the
  // widget's text is read in that language regardless of the host page's lang
  root.lang = locale;

  render(
    <StrictMode>
      <Suspense fallback={null}>
        <StringsProvider strings={strings}>
          <App
            reactRoot={root}
            {...{
              clientIp,
              engagementTrackingUrl,
              sessionId,
              title,
              url,
              bundleId,
              variantId,
              widgetId,
            }}
          />
        </StringsProvider>
      </Suspense>
    </StrictMode>,
    root,
  );
}

async function injectWidgets(
  elementOrSelector?: string | HTMLElement,
  config?: Partial<WidgetConfiguration>,
) {
  const slots = getInjectionPoints(elementOrSelector);
  addInjectionPoints(window.disco, slots);

  injectWidgetsUtil(slots, renderWidget, config);
}

window.disco = createSdk(injectWidgets);

async function initialize() {
  injectWidgets();
}

initialize();
